/**
 * The merchant timezone kernel — i18n-free on purpose.
 *
 * "What day is it for the operator?" is the single fact behind every date the
 * console shows and every date filter it sends. The backend owns it:
 * `/api/system/timezone` serves the session tenant's zone. The gate
 * (`MerchantTimezoneProvider` in `contexts/MerchantTimezoneContext.tsx`)
 * installs it here and renders the signed-in subtree only after it arrived,
 * so every render, event handler and `queryFn` that reads `getMerchantTz()`
 * runs with the zone set (#2875, ADR-0011 decision seven). There is no
 * fallback zone and no cache: reading the zone before it is set throws — a
 * programming error (code running outside the gate), not a runtime state to
 * absorb — and the zone lives in memory only, because a `localStorage` key
 * is shared by every tenant that signs in on the device.
 *
 * This module holds the zone itself — the STATE. The stateless arithmetic over
 * it moved into `@resource-kit/controllers` (#2167, ADR-0035 D1);
 * `merchantWallTimeToUtcIso` is the two-line binding of that arithmetic to
 * this app's zone. #2170 removed the `tzOffsetMsAt` re-export that stood here
 * during the migration: a host path in front of a package symbol is a shim
 * whichever statement mints it, and nothing in this app imported it. Callers
 * that want the raw arithmetic take it from the package. `datetime.ts` holds
 * the LOCALE-aware rendering built on top and re-exports the zone state here,
 * so existing importers are unaffected.
 *
 * Nothing in this file is user-facing, so the fixed `en-US` locale below is a
 * format choice rather than locale policy: it pins ASCII digits and a
 * parseable field order. The repo's ban on a hardcoded locale is about text an
 * operator READS.
 */
import { calendarDayIn, wallTimeToUtcIso } from '@resource-kit/controllers';

let cachedTz: string | null = null;

function isValidIanaTimezone(tz: unknown): tz is string {
  if (typeof tz !== 'string' || !tz) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * The session tenant's zone, as the gate installed it. Throws when no zone has
 * been set: the caller runs outside `MerchantTimezoneProvider`.
 */
export function getMerchantTz(): string {
  if (cachedTz) return cachedTz;
  throw new Error('merchant timezone not loaded: rendered outside MerchantTimezoneProvider');
}

/**
 * Install the zone the backend returned. Memory only. Answers whether the zone
 * was installed: an invalid name is refused and leaves the previous zone in
 * place, so a caller that needs THIS zone must treat `false` as no zone.
 */
export function setMerchantTz(tz: string): boolean {
  if (!isValidIanaTimezone(tz)) {
    console.warn('[merchant-tz] Refusing to set invalid timezone:', tz);
    return false;
  }
  cachedTz = tz;
  return true;
}

/**
 * Today's calendar date (`YYYY-MM-DD`) in the merchant timezone, read from
 * the clock at the moment of the call — the kit's `calendarDayIn` bound to
 * the zone this module owns (the same binding pattern as
 * `merchantWallTimeToUtcIso` below).
 *
 * For NON-render readers only: event handlers and `queryFn`s, which run at
 * the moment that matters and cannot call a hook. A component that shows or
 * requests "today" while it renders reads `useToday()` from
 * `@resource-kit/controllers/react` instead: that hook is subscribed, so it
 * moves to the new day at merchant midnight together with every list (#2786).
 */
export function todayInMerchantTz(now: Date = new Date()): string {
  return calendarDayIn(getMerchantTz(), now);
}

/**
 * The UTC instant a merchant-local WALL CLOCK reading names, as an ISO-8601
 * string — {@link wallTimeToUtcIso} bound to the zone this module owns.
 *
 * The binding is the whole point of the split: the arithmetic is pure and
 * lives in the package, the ZONE is state and lives here, and there is exactly
 * one place (this line) where the two meet for callers that do not carry a
 * zone of their own.
 */
export function merchantWallTimeToUtcIso(wall: string): string {
  return wallTimeToUtcIso(wall, getMerchantTz());
}

/**
 * The UTC instant at which the merchant calendar day `day` (YYYY-MM-DD) began,
 * as an ISO-8601 string — the lower bound for that day against a UTC
 * `created_at` column.
 *
 * Comparing against UTC midnight instead is not a rounding error but a wrong
 * answer: for `America/Los_Angeles` it sits 7-8 hours early, so the previous
 * merchant evening (17:00-24:00 local) counts as "today" for the whole day.
 */
export function merchantDayStartUtc(day: string): string {
  return merchantWallTimeToUtcIso(`${day}T00:00:00.000`);
}
